import { useState } from "react";
import { Link } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacyOutlined";
import { getFirebaseClient, useAuth, AdminDialog, EmptyState } from "@workspace/admin-ui";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { PageHeader } from "@/components/AppShell";
import { chainAdminApi } from "@/lib/callables";
import { useToast } from "@/lib/toast";

interface PharmacyRow {
  id: string;
  displayName: string;
  address: string;
  eircode: string;
  psiNumber: string;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 64);
}

function CreatePharmacyForm({
  chainId,
  onSuccess,
  onCancel,
}: {
  chainId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [eircode, setEircode] = useState("");
  const [psi, setPsi] = useState("");
  const [id, setId] = useState("");
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);

  const onNameChange = (value: string) => {
    setName(value);
    if (!idManuallyEdited) setId(slugify(value.trim()));
  };

  const m = useMutation({
    mutationFn: () =>
      chainAdminApi.createPharmacy({
        chainId,
        name: name.trim(),
        address: address.trim(),
        eircode: eircode.trim(),
        psi: psi.trim(),
        id: id.trim() || undefined,
      }),
    onSuccess: (res) => {
      showSuccess(`Pharmacy ${res.pharmacyId} created.`);
      onSuccess();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed to create pharmacy."),
  });

  const canSubmit = name.trim() && address.trim() && eircode.trim() && psi.trim() && !m.isPending;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField label="Display name *" value={name} onChange={(e) => onNameChange(e.target.value)} fullWidth size="small" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField label="Address *" value={address} onChange={(e) => setAddress(e.target.value)} fullWidth size="small" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField label="Eircode *" value={eircode} onChange={(e) => setEircode(e.target.value)} fullWidth size="small" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField label="PSI number *" value={psi} onChange={(e) => setPsi(e.target.value)} fullWidth size="small" />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Pharmacy ID (slug)"
            helperText="Auto-derived from name — leave blank to use the default."
            value={id}
            onChange={(e) => { setId(e.target.value); setIdManuallyEdited(true); }}
            fullWidth
            size="small"
          />
        </Grid>
      </Grid>
      <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
        <Button variant="text" onClick={onCancel} disabled={m.isPending}>Cancel</Button>
        <Button variant="contained" onClick={() => m.mutate()} disabled={!canSubmit}>
          {m.isPending ? "Creating…" : "Create pharmacy"}
        </Button>
      </Stack>
    </Box>
  );
}

export default function PharmaciesPage() {
  const { membership } = useAuth();
  const chainId = membership?.chainId ?? "";
  const { db } = getFirebaseClient();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const pharmaciesQ = useQuery<PharmacyRow[]>({
    queryKey: ["chain", chainId, "pharmacies", "management-list"],
    queryFn: async () => {
      const snap = await getDocs(
        query(
          collection(db, "chains", chainId, "pharmacies"),
          orderBy("displayName"),
        ),
      );
      return snap.docs.map((d) => {
        const data = d.data() as Record<string, unknown>;
        return {
          id: d.id,
          displayName: String(data.displayName ?? d.id),
          address: String(data.address ?? ""),
          eircode: String(data.eircode ?? ""),
          psiNumber: String(data.psiNumber ?? ""),
        };
      });
    },
    enabled: !!chainId,
  });

  if (!chainId) return null;

  const invalidatePharmacies = () =>
    qc.invalidateQueries({ queryKey: ["chain", chainId, "pharmacies", "management-list"] });

  return (
    <>
      <PageHeader
        title="Pharmacies"
        description="Manage core details, staff, and booking settings for every pharmacy in your chain."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Add pharmacy
          </Button>
        }
      />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper sx={{ overflow: "hidden" }}>
          {pharmaciesQ.isLoading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary" variant="body2">
                Loading pharmacies…
              </Typography>
            </Box>
          ) : !pharmaciesQ.data || pharmaciesQ.data.length === 0 ? (
            <EmptyState
              icon={LocalPharmacyIcon}
              title="No pharmacies yet"
              description="Add your first pharmacy to get started."
              action={
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
                  Add pharmacy
                </Button>
              }
            />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.75rem" }}>
                      Pharmacy
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.75rem" }}>
                      Address
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.75rem" }}>
                      Eircode
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.75rem" }}>
                      PSI No.
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pharmaciesQ.data.map((p) => (
                    <TableRow
                      key={p.id}
                      hover
                      component={Link}
                      href={`/pharmacies/${p.id}`}
                      sx={{ cursor: "pointer", textDecoration: "none" }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {p.displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                          /{p.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {p.address || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {p.eircode ? (
                          <Chip label={p.eircode} size="small" variant="outlined" />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {p.psiNumber || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 1 }}>
                        <ChevronRightIcon sx={{ color: "text.disabled", fontSize: 18 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      <AdminDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add pharmacy"
        subtitle="Adding to your chain"
      >
        <CreatePharmacyForm
          chainId={chainId}
          onSuccess={() => { setAddOpen(false); invalidatePharmacies(); }}
          onCancel={() => setAddOpen(false)}
        />
      </AdminDialog>
    </>
  );
}
